import { Catalogue, CatalogueItem, PriceType } from '../../../common';
import React, { useMemo } from 'react';
import { getCatalogueStats } from '../../../common/helpers/get-catalogue-stats';
import { Field } from 'formik';
import { Basket } from '../basket/basket';
import { BasketEntry } from '../basket-entry/basket-entry';
import { formatCurrencyAmount } from '../../../common/helpers/currency';
import { convertToAutomationId } from 'src/helpers/functional-helpers';

export type BasketInputProps = {
  field: any;
  form: any;
  automationid: string;
  onBasketOpened: (isBasketOpen: boolean) => void;
};

export function BasketInput({ field, form, automationid, onBasketOpened }: BasketInputProps) {
  const catalogueStats = useMemo(
    () => getCatalogueStats(form.values[field.name]),
    [form.values, field.name],
  );

  const { monthly, onceOff } = catalogueStats?.priceTotals || {};

  const monthlyAmount = formatCurrencyAmount(monthly || 0);
  const onceOffAmount = formatCurrencyAmount(onceOff || 0);

  return (
    <>
      <Basket
        numberOfDevices={catalogueStats.deviceTotals.totalDevices || 0}
        basketTotalMonthly={`R ${monthlyAmount}`}
        basketTotalOnceOff={`R ${onceOffAmount}`}
        onBasketOpened={onBasketOpened}
      >
        {form.values[field.name].products
          .filter((product) => product.device.quantity > 0)
          .map((product) => getBasketField(product, form))}
      </Basket>
    </>
  );

  function getBasketField(catalogueItem: CatalogueItem, form) {
    let priceString = '';
    let priceSuffix = '';

    switch (catalogueItem.price.priceType) {
      case PriceType.FREE:
        priceString = 'Free';
        break;
      case PriceType.ONCE_OFF:
        priceString = `R ${formatCurrencyAmount(catalogueItem.price.price)}`;
        priceSuffix = ' / once off';
        break;
      case PriceType.MONTHLY:
        priceString = `R ${formatCurrencyAmount(catalogueItem.price.price)}`;
        priceSuffix = 'pm';
        break;
    }

    const fieldName = `${field.name}.products[${catalogueItem.index}].device.quantity`;

    const total = (field.value as Catalogue).products.reduce((currentTotal, product) => {
      return product.device.quantity ? currentTotal + product.device.quantity : currentTotal;
    }, 0);

    const minimum = total > 1 ? 0 : 1;
    const maximum =
      total >= field?.value?.maxProductsPerOrder
        ? catalogueItem.device.quantity || 0
        : catalogueItem.device.maxQuantity ||
          field?.value?.maxProductsPerOrder - (total - (catalogueItem.device.quantity || 0));

    return (
      <Field
        key={`basket-input-${fieldName}`}
        id={fieldName}
        name={fieldName}
        image={catalogueItem.device.imageBase64}
        title={catalogueItem.device.title}
        priceString={priceString}
        priceSuffix={priceSuffix}
        component={BasketEntry}
        min={minimum}
        max={maximum}
        automationId={`${automationid}_${convertToAutomationId(catalogueItem.device.title)}`}
        onRemove={() => form.setFieldValue(fieldName, 0)}
      />
    );
  }
}
